import { Divider, Typography } from 'antd'
import { useRsfValues } from 'features/indicationTriage/cards/ffsCalc/calculations/calculateRsf'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import {
  DownwardIndicator,
  FfsResultIcon,
} from 'features/indicationTriage/cards/ffsCalc/FfsResultIcon'
import { useSavedReports } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsLtaPublishApi'
import { FfsReportPanel } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/FfsReportPanel'
import {
  FfsAnalysisResultsSection,
  useAnalysisResult,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/FfsResultPanel'
import { FfsPart } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { useFfsScenarios } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/useFfsScenarios'
import { FfsSharedValuesContext } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import { FfsAnalysisResult } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsResultScenarios'
import {
  FfsMawpSection,
  LABEL_MAWP_DESIGN,
  LABEL_MAWP_R,
  useFfsMawpProps,
} from 'features/indicationTriage/cards/ffsCalc/mawpSection/FfsMawpSection'
import { OptionalSection } from 'features/indicationTriage/cards/ffsCalc/optionalSection/OptionalSection'
import { FfsDataValueSection } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsDataValuesSection'
import { useLevelOneFinalAcceptabilityCriteriaValues } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsFinalAcceptabilityCriteraSection'
import {
  FfsFlawSizeCriteriaSection,
  useLevelOneFlawSizeCriteriaCheck,
} from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsFlawSizeCriteriaSection'
import { FfsUserInputSection } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsLevelOneUserInputSection'
import { useDebouncedLoading } from 'features/indicationTriage/cards/ffsCalc/useDebouncedLoading'
import { FfsCalcCardContentProps } from 'features/indicationTriage/cards/FfsCalcCard'
import React, { FC, useContext, useState } from 'react'

import {
  FfsCtpAverageCalc,
  useFfsCtpAverageCalcProps,
} from './ctpRsf/FfsCtpRsf'

const SPIN_DEBOUNCE_DELAY = 300

const { Title } = Typography

const RawFfsLevelTwoCalc: FC<FfsCalcCardContentProps> = () => {
  const {
    fca,
    fcaMl,
    indicationItem,
    ffsDataValues,
    setFca,
    setFcaMl,
    jointEfficiency,
    setJointEfficiency,
    yieldStrength,
    setYieldStrength,
    selectedMawpCalcThickness,
    setSelectedMawpCalcThickness,
    calcType,
    setCalcType,
    designPressure,
  } = useContext(FfsSharedValuesContext)

  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(
    null
  )

  const {
    loading: dataValuesLoading = false,
    tmm = null,
    tc = null,
    innerDiameter = null,
    outerDiameter = null,
    lambda = null,
    nominal = null,
  } = ffsDataValues || {}

  const flawSizeCheckResult = useLevelOneFlawSizeCriteriaCheck({
    fca,
    tmm,
    fcaMl,
    tc,
    d: innerDiameter,
    indicationId: indicationItem?.id,
  })

  const loading = useDebouncedLoading(SPIN_DEBOUNCE_DELAY, [
    flawSizeCheckResult.isLoading,
    dataValuesLoading,
  ])

  const levelOnefinalAcceptabilityCriteraValues =
    useLevelOneFinalAcceptabilityCriteriaValues({
      lambda,
      flawSizeCriteriaCheckValues: flawSizeCheckResult.criteriaValues,
    })
  const { foliasFactor, rsfa } = levelOnefinalAcceptabilityCriteraValues
  const { rsf } = useRsfValues({ tmm, fcaMl, tc, fca, mt: foliasFactor })
  const levelOneMawpProps = useFfsMawpProps({
    t_c: tc,
    t_l: null,
    outerDiameter,
    rsfa,
    rsf,
    designPressure,
    tNom: nominal,
    ffsLevel: '2',
  })

  const {
    reports,
    isLoading: reportsLoading,
    forceReload: reloadReports,
    setDefaultReport,
  } = useSavedReports()

  const longitudinalAxis = 'y'

  const rsfAvgCalProps = useFfsCtpAverageCalcProps({
    area: indicationItem?.selection,
    tc,
    innerDiameter,
    fcaMl,
    longitudinalAxis,
    isLoading: loading,
  })

  const levelTwoFinalAcceptabilityCriteriaValues = {
    ...levelOnefinalAcceptabilityCriteraValues,
    rsf: rsfAvgCalProps.minRsf?.rsf ?? null,
  }

  const { mawpr, mawpDesign } = useFfsMawpProps({
    t_c: tc,
    t_l: null,
    rsf: levelTwoFinalAcceptabilityCriteriaValues.rsf ?? null,
    outerDiameter,
    rsfa,
    designPressure,
    ffsLevel: '2',
  })

  // Calculate the analysis result for this level
  const analysisResult: FfsAnalysisResult = useAnalysisResult({
    mawpr,
    mawpDesign,
    resultProps: {
      rsf: levelTwoFinalAcceptabilityCriteriaValues.rsf,
      flawSizeCheckResult,
    },
  })

  // Hook to manage scenario selection and value synchronization
  useFfsScenarios({
    selectedScenarioId,
    setSelectedScenarioId,
    rsf_a: levelTwoFinalAcceptabilityCriteriaValues.rsfa ?? null,
    setRsf_a: () => {}, // Level 2 RSF is computed, not directly settable
    e: levelOneMawpProps.qualityFactor,
    setE: levelOneMawpProps.setQualityFactor || (() => {}),
    y: levelOneMawpProps.pressureCoefficient,
    setY: levelOneMawpProps.setPressureCoefficient,
    w: jointEfficiency,
    setW: setJointEfficiency,
    s: yieldStrength,
    setS: setYieldStrength,
    selectedMawpCalcThickness: selectedMawpCalcThickness,
    setSelectedMawpCalcThickness: setSelectedMawpCalcThickness,
    tsl: levelOneMawpProps.tsl,
    setTsl: levelOneMawpProps.setTsl || (() => {}),
    fca,
    setFca,
    fca_ml: fcaMl,
    setFca_ml: setFcaMl,
    scenarios: reports,
    calcType,
    setCalcType,
    ffsLevel: '2',
    ffsPart: FfsPart.PART_5,
  })

  return (
    <>
      <Title level={4}>User Parameters</Title>
      <FfsUserInputSection />
      <Divider />
      <Title level={4}>Data Values</Title>
      {ffsDataValues && <FfsDataValueSection {...ffsDataValues} />}
      <Divider />
      <Title level={4}>Flaw Size Criteria</Title>
      <FfsFlawSizeCriteriaSection
        flawSizeCheckResult={flawSizeCheckResult}
        loading={loading}
      />
      <Divider>
        <FfsResultIcon
          passed={!flawSizeCheckResult.failingCriteria.size}
          tooltipFailureText='Failed acceptability criteria. Derating required (below)'
          trailingPass={DownwardIndicator}
        />
      </Divider>
      <OptionalSection visible={!flawSizeCheckResult.failingCriteria.size}>
        <Title level={4}>MAWP</Title>
        <FfsMawpSection
          {...levelOneMawpProps}
          addLongitudinalStress={false}
          showMawpCriteria={true}
          mawpr={null}
          loading={loading}
          ffsLevel={'2'}
        />
        <Divider />
        <Title level={4}>Critical Profile Thickness</Title>
        <FfsCtpAverageCalc {...rsfAvgCalProps} />
        <Divider>
          <FfsResultIcon
            passed={rsfAvgCalProps.passesFac}
            tooltipFailureText='Failed acceptability criteria. Derating required (below)'
            trailingFail={DownwardIndicator}
          />
        </Divider>
        <OptionalSection visible={!rsfAvgCalProps.passesFac}>
          <FfsParameterField
            label={LABEL_MAWP_DESIGN}
            labelTooltip='Design pressure (MAWP) for the component'
            value={mawpDesign}
            measurementType='pressure'
          />
          <FfsParameterField
            label={LABEL_MAWP_R}
            labelTooltip='Re-rated maximum allowable working pressure'
            value={mawpr}
            measurementType='pressure'
          />
        </OptionalSection>
      </OptionalSection>
      <Divider />
      <Title level={4}>Results</Title>
      <FfsAnalysisResultsSection analysisResult={analysisResult} />
      <Divider />
      <Title level={4}>Reports</Title>
      <FfsReportPanel
        setSelectedScenarioId={(id) => setSelectedScenarioId(id ?? null)}
        flawSizeCriteriaCheck={flawSizeCheckResult}
        finalAcceptabilityCriteriaCheck={
          levelTwoFinalAcceptabilityCriteriaValues
        }
        mawpProps={levelOneMawpProps}
        ffsPart={FfsPart.PART_5}
        ffsLevel='2'
        reports={reports}
        reportsLoading={reportsLoading}
        reloadReports={reloadReports}
        setDefaultReport={setDefaultReport}
        selectedScenarioId={selectedScenarioId}
        analysisResult={analysisResult}
      />
    </>
  )
}

export const FfsPartFiveLevelTwoCalc = React.memo(RawFfsLevelTwoCalc)
